import { useState } from "react";
import { makeTr } from "@/i18n/tr";
import { Link, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { toast } from "sonner";
import { Crown, Mail, Lock, ArrowLeft, Loader2, Send, CheckCircle, KeyRound } from "lucide-react";
import { API_URL } from "@/config/api";

export default function ForgotPasswordPage() {
  const { t, i18n } = useTranslation();
  const isRu = i18n.language === "ru";
  const tr = makeTr(i18n.language);
  const navigate = useNavigate();
  
  const [step, setStep] = useState(1); // 1: email, 2: code, 3: success
  const [email, setEmail] = useState("");
  const [code, setCode] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [loading, setLoading] = useState(false);

  const handleRequestCode = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      const response = await fetch(`${API_URL}/api/auth/password-reset/request`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email })
      });
      
      const data = await response.json();
      
      if (response.ok) {
        toast.success(tr("Код отправлен", "Code sent"), {
          description: tr("Проверьте ваш Telegram", "Check your Telegram")
        });
        setStep(2);
      } else {
        toast.error(tr("Ошибка", "Error"), {
          description: data.detail || (tr("Не удалось отправить код", "Failed to send code"))
        });
      }
    } catch (error) {
      toast.error(tr("Ошибка сети", "Network error"));
    }
    
    setLoading(false);
  };

  const handleResetPassword = async (e) => {
    e.preventDefault();
    
    if (newPassword !== confirmPassword) {
      toast.error(tr("Пароли не совпадают", "Passwords don't match"));
      return;
    }
    
    if (newPassword.length < 6) {
      toast.error(tr("Пароль должен быть минимум 6 символов", "Password must be at least 6 characters"));
      return;
    }
    
    setLoading(true);

    try {
      const response = await fetch(`${API_URL}/api/auth/password-reset/verify`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ 
          email, 
          code, 
          new_password: newPassword 
        })
      });
      
      const data = await response.json();
      
      if (response.ok) {
        setStep(3);
        toast.success(tr("Пароль изменён", "Password changed"));
      } else {
        toast.error(tr("Ошибка", "Error"), {
          description: data.detail || (tr("Неверный код", "Invalid code"))
        });
      }
    } catch (error) {
      toast.error(tr("Ошибка сети", "Network error"));
    }
    
    setLoading(false);
  };

  return (
    <div className="min-h-screen bg-background noise-bg flex items-center justify-center px-4">
      {/* Background */}
      <div 
        className="absolute inset-0 z-0"
        style={{
          backgroundImage: `linear-gradient(180deg, rgba(9, 9, 11, 0.95) 0%, rgba(9, 9, 11, 0.98) 100%), 
                            url('https://images.unsplash.com/photo-1528819622765-d6bcf132f793?w=1920&q=80')`,
          backgroundSize: "cover",
          backgroundPosition: "center",
        }}
      />

      <div className="w-full max-w-md relative z-10">
        {/* Back link */}
        <Link 
          to="/login" 
          className="inline-flex items-center gap-2 text-muted-foreground hover:text-foreground mb-8 transition-colors"
        >
          <ArrowLeft className="h-4 w-4" />
          {tr("Назад к входу", "Back to login")}
        </Link>

        <Card className="glass border-border/50">
          <CardHeader className="text-center space-y-4">
            <Link to="/" className="flex items-center justify-center gap-2">
              <Crown className="h-10 w-10 text-primary" />
            </Link>
            <div>
              <CardTitle className="text-2xl font-black">
                {step === 3 
                  ? (tr("Готово!", "Done!")) 
                  : (tr("Восстановление пароля", "Password Recovery"))}
              </CardTitle>
              <CardDescription>
                {step === 1 && (tr("Введите email для получения кода в Telegram", "Enter email to get code via Telegram"))}
                {step === 2 && (tr("Введите код из Telegram и новый пароль", "Enter code from Telegram and new password"))}
                {step === 3 && (tr("Ваш пароль успешно изменён", "Your password has been changed"))}
              </CardDescription>
            </div>
          </CardHeader>
          
          <CardContent>
            {/* Step 1: Email */}
            {step === 1 && (
              <form onSubmit={handleRequestCode} className="space-y-6">
                <div className="space-y-2">
                  <Label htmlFor="email">{tr("Email", "Email")}</Label>
                  <div className="relative">
                    <Mail className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                    <Input
                      id="email"
                      type="email"
                      placeholder="email@example.com"
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      className="pl-10 h-12 bg-input/50"
                      required
                      data-testid="reset-email"
                    />
                  </div>
                  <p className="text-xs text-muted-foreground">
                    {isRu 
                      ? "К аккаунту должен быть привязан Telegram" 
                      : "Telegram must be linked to your account"}
                  </p>
                </div>

                <Button 
                  type="submit" 
                  className="w-full btn-gold h-12 text-base"
                  disabled={loading}
                  data-testid="reset-submit"
                >
                  {loading ? (
                    <>
                      <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                      {tr("Отправка...", "Sending...")}
                    </>
                  ) : (
                    <>
                      <Send className="mr-2 h-4 w-4" />
                      {tr("Отправить код", "Send Code")}
                    </>
                  )}
                </Button>
              </form>
            )}

            {/* Step 2: Code + New Password */}
            {step === 2 && (
              <form onSubmit={handleResetPassword} className="space-y-6">
                <div className="space-y-2">
                  <Label htmlFor="code">{tr("Код из Telegram", "Code from Telegram")}</Label>
                  <div className="relative">
                    <KeyRound className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                    <Input
                      id="code"
                      type="text"
                      placeholder="123456"
                      value={code}
                      onChange={(e) => setCode(e.target.value)}
                      className="pl-10 h-12 bg-input/50 text-center text-lg tracking-widest"
                      maxLength={6}
                      required
                      data-testid="reset-code"
                    />
                  </div>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="newPassword">{tr("Новый пароль", "New Password")}</Label>
                  <div className="relative">
                    <Lock className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                    <Input
                      id="newPassword"
                      type="password"
                      placeholder="••••••••"
                      value={newPassword}
                      onChange={(e) => setNewPassword(e.target.value)}
                      className="pl-10 h-12 bg-input/50"
                      minLength={6}
                      required
                      data-testid="reset-new-password"
                    />
                  </div>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="confirmPassword">{tr("Повторите пароль", "Confirm Password")}</Label>
                  <div className="relative">
                    <Lock className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                    <Input
                      id="confirmPassword"
                      type="password"
                      placeholder="••••••••"
                      value={confirmPassword}
                      onChange={(e) => setConfirmPassword(e.target.value)}
                      className="pl-10 h-12 bg-input/50"
                      minLength={6}
                      required
                      data-testid="reset-confirm-password"
                    />
                  </div>
                </div>

                <div className="flex gap-3">
                  <Button 
                    type="button" 
                    variant="outline"
                    className="flex-1 h-12"
                    onClick={() => setStep(1)}
                  >
                    {tr("Назад", "Back")}
                  </Button>
                  <Button 
                    type="submit" 
                    className="flex-1 btn-gold h-12"
                    disabled={loading}
                    data-testid="reset-password-submit"
                  >
                    {loading ? (
                      <Loader2 className="h-4 w-4 animate-spin" />
                    ) : (
                      tr("Изменить", "Change")
                    )}
                  </Button>
                </div>

                <button
                  type="button"
                  onClick={handleRequestCode}
                  className="w-full text-sm text-muted-foreground hover:text-primary transition-colors"
                >
                  {tr("Отправить код повторно", "Resend code")}
                </button>
              </form>
            )}

            {/* Step 3: Success */}
            {step === 3 && (
              <div className="text-center space-y-6">
                <div className="w-16 h-16 rounded-full bg-green-500/20 flex items-center justify-center mx-auto">
                  <CheckCircle className="h-8 w-8 text-green-500" />
                </div>
                
                <p className="text-muted-foreground">
                  {isRu 
                    ? "Теперь вы можете войти с новым паролем" 
                    : "You can now login with your new password"}
                </p>

                <Button 
                  onClick={() => navigate("/login")}
                  className="w-full btn-gold h-12 text-base"
                  data-testid="go-to-login"
                >
                  {tr("Войти", "Login")}
                </Button>
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
